import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Polyline, Popup, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api, imageUrl, API } from "@/lib/api";
import { ArrowLeft, MapPin, Broadcast, Trash, Share, Copy, Code } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function FitRoute({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 12);
    else if (points.length > 1) {
      try { map.fitBounds(points, { padding: [40, 40], maxZoom: 13 }); } catch (e) { /* */ }
    }
  }, [points, map]);
  return null;
}

export default function StoryDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);

  useEffect(() => { api.get(`/stories/${id}`).then((r) => setS(r.data)).catch(() => nav("/stories")); }, [id, nav]);
  if (!s) return <div className="grid h-64 place-items-center text-muted-foreground">Loading…</div>;

  const publish = async () => { await api.post(`/stories/${id}/publish`); setS({ ...s, status: "published" }); toast.success("Published"); };
  const del = async () => { await api.delete(`/stories/${id}`); toast.success("Deleted"); nav("/stories"); };

  const waypoints = (s.sections || []).filter((x) => x.lat != null && x.lon != null);
  const routePts = waypoints.map((w) => [w.lat, w.lon]);
  const shareUrl = `${window.location.origin}/s/${s.id}`;
  const embedCode = `<iframe src="${shareUrl}" width="100%" height="720" style="border:0" loading="lazy"></iframe>`;
  const copy = (v, msg) => { navigator.clipboard.writeText(v); toast.success(msg); };

  return (
    <div className="mx-auto max-w-4xl">
      <button onClick={() => nav("/stories")} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-zinc-900">
        <ArrowLeft size={16} weight="bold" /> All stories
      </button>

      {/* Cover */}
      <div className="relative overflow-hidden rounded-md">
        <img src={imageUrl(s.cover_photo_id)} alt="" className="h-72 w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute bottom-0 p-6 text-white">
          <span className={`mb-2 inline-block rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase ${s.status === "published" ? "bg-[#F27A54]" : "bg-amber-500"}`}>{s.status}</span>
          <h1 className="font-display text-3xl font-black leading-tight tracking-tight drop-shadow">{s.title}</h1>
          <p className="mt-1 flex items-center gap-1 text-sm text-white/85"><MapPin size={14} weight="fill" />{[s.city, s.country].filter(Boolean).join(", ")}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {s.status !== "published" && <Button onClick={publish} className="gap-1.5 rounded-md bg-[#F27A54] hover:bg-[#d9633f]" data-testid="story-publish-btn"><Broadcast size={15} weight="bold" />Publish Story</Button>}
        <Button variant="outline" onClick={del} className="gap-1.5 rounded-md border-red-200 text-red-600 hover:bg-red-50"><Trash size={15} />Delete</Button>
      </div>

      {/* Share / embed (published) */}
      {s.status === "published" && (
        <div className="mt-4 rounded-md border border-[#F27A54]/20 bg-blue-50/50 p-4" data-testid="story-share-box">
          <div className="flex items-center gap-2"><Share size={16} weight="fill" className="text-[#F27A54]" /><h3 className="font-display text-sm font-bold">Share & embed on your sites</h3></div>
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2">
              <input readOnly value={shareUrl} className="h-9 flex-1 rounded-md border border-border bg-white px-3 font-mono-p text-xs" />
              <Button size="sm" variant="outline" className="h-9 gap-1 rounded-md" onClick={() => copy(shareUrl, "Link copied")} data-testid="copy-share-link"><Copy size={13} />Link</Button>
              <a href={`/s/${s.id}`} target="_blank" rel="noreferrer"><Button size="sm" className="h-9 rounded-md bg-[#F27A54] hover:bg-[#d9633f]">Open</Button></a>
            </div>
            <div className="flex items-center gap-2">
              <input readOnly value={embedCode} className="h-9 flex-1 rounded-md border border-border bg-white px-3 font-mono-p text-xs" />
              <Button size="sm" variant="outline" className="h-9 gap-1 rounded-md" onClick={() => copy(embedCode, "Embed code copied")} data-testid="copy-embed-code"><Code size={13} />Embed</Button>
            </div>
          </div>
        </div>
      )}

      <p className="mt-6 text-lg leading-relaxed text-zinc-700">{s.intro}</p>

      {/* Journey mini-map */}
      {routePts.length > 0 && (
        <div className="mt-6">
          <h2 className="mb-2 flex items-center gap-1.5 font-display text-lg font-bold"><MapPin size={16} weight="fill" className="text-[#F27A54]" />The Journey</h2>
          <div className="h-72 overflow-hidden rounded-md border border-border" data-testid="story-map">
            <MapContainer center={routePts[0]} zoom={12} scrollWheelZoom={false} className="h-full w-full">
              <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <FitRoute points={routePts} />
              {routePts.length > 1 && <Polyline positions={routePts} pathOptions={{ color: "#F27A54", weight: 3, dashArray: "6 6" }} />}
              {waypoints.map((w, i) => (
                <CircleMarker key={i} center={[w.lat, w.lon]} radius={9}
                  pathOptions={{ color: "#fff", weight: 2, fillColor: "#F27A54", fillOpacity: 1 }}>
                  <Tooltip permanent direction="top" offset={[0, -8]} className="!rounded-sm">{i + 1}. {w.heading}</Tooltip>
                  <Popup>{w.heading}</Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </div>
      )}

      <div className="mt-8 space-y-10">
        {s.sections?.map((sec, i) => (
          <section key={i}>
            <h2 className="font-display text-xl font-bold tracking-tight">{sec.heading}</h2>
            {sec.blurb && <p className="mt-1 text-sm leading-relaxed text-zinc-600">{sec.blurb}</p>}
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {sec.photo_ids?.map((pid) => (
                <Link key={pid} to={`/photos/${pid}`} className="overflow-hidden rounded-md border border-border">
                  <img src={imageUrl(pid)} alt="" className="aspect-square w-full object-cover transition-transform duration-300 hover:scale-105" />
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
